import type { Task } from "@app/core";
import { useState } from "react";
import { createPortal } from "react-dom";
import {
  useCalendarsStore,
  useGoogleAuthStore,
  useGoogleSyncStore,
  useLabelsStore,
  useTopicsStore,
} from "../../stores/index.js";

interface Props {
  task: Task;
  onClose: () => void;
}

export function TaskDebugDialog({ task, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const calendars = useCalendarsStore((s) => s.calendars);
  const labels = useLabelsStore((s) => s.labels);
  const topics = useTopicsStore((s) => s.topics);
  const googleConnected = useGoogleAuthStore((s) => s.connected);
  const googleEmail = useGoogleAuthStore((s) => s.email);
  const googleLastSync = useGoogleSyncStore((s) => s.lastSyncAt);
  const googleBusy = useGoogleSyncStore((s) => s.busy);
  const googleLastError = useGoogleSyncStore((s) => s.lastError);

  const calendar = calendars.find((c) => c.id === task.calendar_id);
  const taskLabels = labels.filter((l) => task.label_ids.includes(l.id));
  const taskTopics = topics.filter((t) => task.topic_ids.includes(t.id));
  const googleCal = calendar?.external_source === "google" ? calendar : null;

  function handleCopy() {
    const data = {
      task,
      resolved: {
        calendarName: calendar?.name ?? null,
        labelNames: taskLabels.map((l) => l.name),
        topicNames: taskTopics.map((t) => t.name),
      },
      googleSync: {
        account: { connected: googleConnected, email: googleEmail },
        store: { busy: googleBusy, lastSyncAt: googleLastSync, lastError: googleLastError },
        calendarBinding: googleCal
          ? {
              name: googleCal.name,
              external_calendar_id: googleCal.external_calendar_id,
              external_account: googleCal.external_account,
              sync_direction: googleCal.sync_direction,
              sync_token: googleCal.sync_token,
              last_synced_at: googleCal.last_synced_at,
            }
          : null,
      },
    };
    const text = JSON.stringify(data, null, 2);
    copyToClipboard(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center overflow-auto bg-black/60 p-4 pt-12"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="font-mono text-sm font-semibold text-muted-foreground">
            debug: {task.title || "(untitled)"}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              {copied ? "Copied!" : "Copy JSON"}
            </button>
            <button
              onClick={onClose}
              className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              ✕ Close
            </button>
          </div>
        </div>

        <div className="max-h-[76vh] space-y-4 overflow-y-auto p-4 font-mono text-xs">
          <Sect title="Identity">
            <KV k="id" v={task.id} />
            <KV k="virtual" v={task.virtual ?? false} />
            <KV k="parent_id" v={task.parent_id} />
            <KV k="recurrence_id" v={task.recurrence_id} />
          </Sect>

          <Sect title="Core">
            <KV k="title" v={task.title} />
            <KV k="status" v={task.status} />
            <KV k="priority" v={task.priority} />
            <KV k="is_event" v={task.is_event} />
            <KV k="custom_color" v={task.custom_color} />
            <KV k="location" v={task.location || null} />
            <KV
              k="notes"
              v={task.notes ? task.notes.slice(0, 120) + (task.notes.length > 120 ? "…" : "") : null}
            />
          </Sect>

          <Sect title="Schedule">
            <KV k="due_at" v={task.due_at} />
            <KV k="all_day" v={task.all_day} />
            <KV k="duration_minutes" v={task.duration_minutes} />
            <KV k="deadline_at" v={task.deadline_at} />
            <KV k="snoozed_until" v={task.snoozed_until} />
            <KV k="completed_at" v={task.completed_at} />
            <KV k="tz" v={task.tz} />
          </Sect>

          <Sect title="Organization">
            <KV k="calendar_id" v={task.calendar_id} />
            <KV k="calendar_name" v={calendar?.name ?? "(not found)"} />
            <KV k="label_ids" v={task.label_ids} />
            <KV k="labels" v={taskLabels.map((l) => l.name)} />
            <KV k="topic_ids" v={task.topic_ids} />
            <KV k="topics" v={taskTopics.map((t) => t.name)} />
          </Sect>

          <Sect title="Subtasks">
            <KV k="subtask_parent_id" v={task.subtask_parent_id} />
            <KV k="subtask_order" v={task.subtask_order} />
          </Sect>

          <Sect title="Multi-device Sync">
            <KV k="rev" v={task.rev} />
            <KV k="ical_uid" v={task.ical_uid} />
          </Sect>

          <Sect title="Google Calendar Sync">
            <KV k="account.connected" v={googleConnected} />
            <KV k="account.email" v={googleEmail} />
            <KV k="store.busy" v={googleBusy} />
            <KV k="store.lastSyncAt" v={googleLastSync} />
            <KV k="store.lastError" v={googleLastError} />
            <div className="mt-1 border-t border-border/40 pt-1" />
            <KV k="task.source" v={task.source} />
            <KV k="task.external_id" v={task.external_id} />
            <KV k="task.etag" v={task.etag} />
            <KV k="task.external_calendar_id" v={task.external_calendar_id} />
            {googleCal ? (
              <>
                <div className="mt-1 border-t border-border/40 pt-1" />
                <KV k="cal.name" v={googleCal.name} />
                <KV k="cal.external_calendar_id" v={googleCal.external_calendar_id} />
                <KV k="cal.external_account" v={googleCal.external_account} />
                <KV k="cal.sync_direction" v={googleCal.sync_direction} />
                <KV k="cal.sync_token" v={googleCal.sync_token} />
                <KV k="cal.last_synced_at" v={googleCal.last_synced_at} />
              </>
            ) : (
              <KV k="calendar binding" v="(none — calendar not bound to Google)" />
            )}
          </Sect>

          <Sect title="Timestamps">
            <KV k="created_at" v={task.created_at} />
            <KV k="updated_at" v={task.updated_at} />
            <KV k="deleted_at" v={task.deleted_at} />
            <KV k="mut" v={String(task.mut ?? 0)} />
            <KV k="server_acked_mut" v={String(task.server_acked_mut ?? 0)} />
            <KV k="google_acked_mut" v={String(task.google_acked_mut ?? 0)} />
          </Sect>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function copyToClipboard(text: string) {
  // Run execCommand synchronously while still inside the user-gesture (click)
  // context — the async clipboard API rejects AFTER the gesture window closes,
  // so its .catch fallback arrives too late for execCommand to work.
  if (execCommandCopy(text)) return;
  // Modern API as a backup for browsers that block execCommand.
  navigator.clipboard?.writeText(text).catch(() => {});
}

function execCommandCopy(text: string): boolean {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function Sect({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[calc(10px*var(--app-text-scale))] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {title}
      </div>
      <div className="space-y-px">{children}</div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: unknown }) {
  let display: React.ReactNode;
  if (v === null || v === undefined) {
    display = <span className="text-muted-foreground/40">null</span>;
  } else if (typeof v === "boolean") {
    display = <span className={v ? "text-emerald-500 dark:text-emerald-400" : "text-muted-foreground"}>{String(v)}</span>;
  } else if (typeof v === "number") {
    display = <span className="text-sky-500 dark:text-sky-400">{v}</span>;
  } else if (Array.isArray(v)) {
    display =
      v.length === 0 ? (
        <span className="text-muted-foreground/40">[]</span>
      ) : (
        <span className="text-card-foreground">{JSON.stringify(v)}</span>
      );
  } else {
    display = <span className="break-all text-card-foreground">{String(v)}</span>;
  }

  return (
    <div className="flex gap-2 rounded px-1 py-0.5 hover:bg-accent/30">
      <span className="w-48 shrink-0 text-muted-foreground">{k}</span>
      {display}
    </div>
  );
}
